import { Form, Spin } from 'antd';
import type { FormInstance } from 'antd';
import { useContext, useEffect } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { CONTACTS_SECTIONS } from 'app/components/commons/Hotel/Contacts/ContactSection';
import { Mode } from 'app/components/commons/Hotel/Types';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotelContacts } from 'app/hooks/data/core/extranet/hotel/useHotelContacts';
import type { ExtranetHotelContacts } from 'app/typings/core/extranet/hotel/contacts';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import styles from './Contacts.module.scss';

type BuilderContactProps = {
  hotelId: number;
  form: FormInstance<ExtranetHotelContacts>;
  onFinish: (values: ExtranetHotelContacts) => void;
};

const ContactsComponent = ({
  hotelId,
  form,
  onFinish,
}: BuilderContactProps) => {
  const { mode, isViewMode, enterEditMode } = useContext(ViewEditModeContext);

  const { data: hotelContacts, isLoading: isHotelContactsLoading } =
    useHotelContacts(hotelId);

  useEffect(() => {
    enterEditMode();
  }, [enterEditMode]);

  useEffect(() => {
    if (hotelContacts) {
      form.setFieldsValue(hotelContacts);
    }
  }, [hotelContacts, form]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      disabled={mode === Mode.View}
      className={styles.contactForm}
      scrollToFirstError={SCROLL_TO_FIRST_ERROR}
    >
      <Spin spinning={isHotelContactsLoading}>
        {CONTACTS_SECTIONS.filter(
          (section) => section.key !== 'space-contacts'
        ).map((section) => {
          const SectionComponent = section.component;

          return (
            <SCard
              key={section.key}
              title={section.title}
              subtitle={section.subtitle}
              className={styles.contactSection}
            >
              <SectionComponent
                isEditMode={!isViewMode}
                hotelId={hotelId}
                form={form}
                hideComment
              />
            </SCard>
          );
        })}
      </Spin>
      <div className={styles.bottomSpacer} />
    </Form>
  );
};

export const BuilderContact = ({
  hotelId,
  form,
  onFinish,
}: BuilderContactProps) => (
  <ViewEditModeContextProvider>
    <ContactsComponent hotelId={hotelId} form={form} onFinish={onFinish} />
  </ViewEditModeContextProvider>
);
